import { useMemo, useState } from 'react';
import {
  Heart, Building2, User as UserIcon, MapPin, Sun, Home as HomeIcon, Lightbulb,
} from 'lucide-react';
import { useFavorites } from '../../hooks/useAPI';
import FavoriteButton from './FavoriteButton';
import { useAuthStore } from '../../store/authStore';

interface FavoritesScreenProps {
  onNavigate: (screen: any, params?: any) => void;
}

type Tab = 'all' | 'clubs' | 'courts' | 'coaches';

/**
 * Shows every starred item for the signed-in user across all three
 * target kinds, grouped by kind with tabs to filter. Unauthenticated
 * users see a sign-in prompt instead.
 */
export default function FavoritesScreen({ onNavigate }: FavoritesScreenProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: favourites, isLoading } = useFavorites();
  const [tab, setTab] = useState<Tab>('all');

  const rows = (favourites as any[]) ?? [];

  const { clubs, courts, coaches } = useMemo(() => {
    const clubs   = rows.filter((r) => !!r.club_id  && r.clubs);
    const courts  = rows.filter((r) => !!r.court_id && r.courts);
    const coaches = rows.filter((r) => !!r.coach_id && r.coaches);
    return { clubs, courts, coaches };
  }, [rows]);

  const total = clubs.length + courts.length + coaches.length;

  if (!isAuthenticated) {
    return (
      <div className="w-full min-h-screen bg-background pb-24">
        <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] text-white px-6 pt-6 pb-6 rounded-b-[2rem]">
          <h1 className="text-2xl mb-1">Favourites</h1>
          <p className="text-sm opacity-80">Save the places and coaches you love.</p>
        </div>
        <div className="px-6 mt-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-secondary flex items-center justify-center mb-3">
            <Heart className="w-7 h-7 text-muted-foreground" />
          </div>
          <h2 className="text-base font-medium">Sign in to keep a list</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            Your favourites sync across devices once you're signed in.
          </p>
          <button
            onClick={() => onNavigate('login')}
            className="mt-5 h-11 px-5 rounded-xl bg-[#1A6B4F] text-white text-sm"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-background pb-24">
      <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] text-white px-6 pt-6 pb-6 rounded-b-[2rem]">
        <h1 className="text-2xl mb-1">Favourites</h1>
        <p className="text-sm opacity-80">
          {total === 0 ? 'Tap the heart on anything to save it here.' : `${total} saved`}
        </p>
      </div>

      <div className="px-6 mt-4 mb-4 flex gap-2 overflow-x-auto no-scrollbar">
        <TabChip active={tab === 'all'}     onClick={() => setTab('all')}     label={`All (${total})`} />
        <TabChip active={tab === 'clubs'}   onClick={() => setTab('clubs')}   label={`Clubs (${clubs.length})`} />
        <TabChip active={tab === 'courts'}  onClick={() => setTab('courts')}  label={`Courts (${courts.length})`} />
        <TabChip active={tab === 'coaches'} onClick={() => setTab('coaches')} label={`Coaches (${coaches.length})`} />
      </div>

      <div className="px-6 space-y-6">
        {isLoading ? (
          <div className="text-center text-sm text-muted-foreground py-12">Loading your favourites…</div>
        ) : total === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-12">
            <Heart className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
            <div>No favourites yet.</div>
            <button
              onClick={() => onNavigate('home')}
              className="mt-3 text-sm text-[#1A6B4F] hover:underline"
            >
              Browse tennis clubs, courts, and coaches →
            </button>
          </div>
        ) : (
          <>
            {(tab === 'all' || tab === 'clubs') && clubs.length > 0 && (
              <FavSection title="Clubs">
                {clubs.map((r: any) => (
                  <ClubRow key={r.id} fav={r} onClick={() => onNavigate('club', { id: r.clubs.id })} />
                ))}
              </FavSection>
            )}
            {(tab === 'all' || tab === 'courts') && courts.length > 0 && (
              <FavSection title="Courts">
                {courts.map((r: any) => (
                  <CourtRow key={r.id} fav={r} onClick={() => onNavigate('court', { id: r.courts.id })} />
                ))}
              </FavSection>
            )}
            {(tab === 'all' || tab === 'coaches') && coaches.length > 0 && (
              <FavSection title="Coaches">
                {coaches.map((r: any) => (
                  <CoachRow key={r.id} fav={r} onClick={() => onNavigate('coach', { id: r.coaches.id })} />
                ))}
              </FavSection>
            )}
            {tab !== 'all' && ((tab === 'clubs' && clubs.length === 0)
              || (tab === 'courts' && courts.length === 0)
              || (tab === 'coaches' && coaches.length === 0)) && (
              <div className="text-center text-sm text-muted-foreground py-6">
                Nothing in this category yet.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function TabChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 h-8 px-3 rounded-full text-xs whitespace-nowrap transition-colors ${
        active ? 'bg-[#1A6B4F] text-white' : 'bg-card border border-border'
      }`}
    >
      {label}
    </button>
  );
}

function FavSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function ClubRow({ fav, onClick }: { fav: any; onClick: () => void }) {
  const club = fav.clubs;
  const cover = Array.isArray(club.photos) && club.photos[0];
  return (
    <button onClick={onClick}
      className="w-full text-left bg-card border border-border rounded-2xl p-3 flex gap-3 items-center hover:border-[#1A6B4F]/40 transition-colors">
      <div className="w-14 h-14 rounded-xl bg-secondary overflow-hidden flex items-center justify-center shrink-0">
        {club.logo_url
          ? <img src={club.logo_url} alt="" className="w-full h-full object-cover" />
          : cover
            ? <img src={cover} alt="" className="w-full h-full object-cover" />
            : <Building2 className="w-5 h-5 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{club.name}</div>
        {club.address && (
          <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
            <MapPin className="w-3 h-3" />{club.address}
          </div>
        )}
      </div>
      <FavoriteButton target={{ kind: 'club', id: club.id }} variant="icon" isFavouritedHint />
    </button>
  );
}

function CourtRow({ fav, onClick }: { fav: any; onClick: () => void }) {
  const court = fav.courts;
  const club = court?.clubs;
  return (
    <button onClick={onClick}
      className="w-full text-left bg-card border border-border rounded-2xl p-3 flex gap-3 items-center hover:border-[#1A6B4F]/40 transition-colors">
      <div className="w-14 h-14 rounded-xl bg-secondary overflow-hidden flex items-center justify-center shrink-0">
        {Array.isArray(court.photos) && court.photos[0]
          ? <img src={typeof court.photos[0] === 'string' ? court.photos[0] : court.photos[0].url}
                 alt="" className="w-full h-full object-cover" />
          : <Building2 className="w-5 h-5 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{club?.name ?? 'Court'}</div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap capitalize">
          {court.name} · {court.surface}
          <span className="inline-flex items-center gap-1">
            {court.is_indoor ? <HomeIcon className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
            {court.is_indoor ? 'Indoor' : 'Outdoor'}
          </span>
          {court.has_lights && <span className="inline-flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Lights</span>}
        </div>
        <div className="text-xs text-[#1A6B4F] mt-0.5">
          from {court.currency ?? 'AED'} {Number(court.hourly_rate_offpeak ?? 0).toFixed(0)}/hr
        </div>
      </div>
      <FavoriteButton target={{ kind: 'court', id: court.id }} variant="icon" isFavouritedHint />
    </button>
  );
}

function CoachRow({ fav, onClick }: { fav: any; onClick: () => void }) {
  const c = fav.coaches;
  return (
    <button onClick={onClick}
      className="w-full text-left bg-card border border-border rounded-2xl p-3 flex gap-3 items-center hover:border-[#1A6B4F]/40 transition-colors">
      <div className="w-14 h-14 rounded-xl bg-secondary overflow-hidden flex items-center justify-center shrink-0">
        {c.photo_url
          ? <img src={c.photo_url} alt="" className="w-full h-full object-cover" />
          : <UserIcon className="w-5 h-5 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{c.display_name}</div>
        <div className="text-xs text-muted-foreground capitalize flex items-center gap-2">
          {c.type === 'freelance' ? 'Freelance coach' : 'Staff coach'}
          {c.years_experience && <span>· {c.years_experience}y experience</span>}
        </div>
      </div>
      <FavoriteButton target={{ kind: 'coach', id: c.id }} variant="icon" isFavouritedHint />
    </button>
  );
}
